import type { EmblaOptionsType } from 'embla-carousel';

export interface CarouselPresetConfig {
  /** Default Embla options for this preset */
  opts: Partial<EmblaOptionsType>;
  /** Tailwind classes for secondary elements */
  classNames?: {
    /** Flex container (gap, margin, etc.) */
    container?: string;
    /** Each slide item (flex-basis sizing, spacing, etc.) */
    item?: string;
  };
  /** Whether to apply backfaceVisibility: hidden on the content container (default: true) */
  backfaceHidden?: boolean;
}

export type CarouselPresetName = 'fullWidth' | 'cardSmall' | 'cardStandard' | 'cardLarge';

/**
 * Card sizes from Figma spec:
 *
 * | Variant  | Mobile       | Desktop      |
 * |----------|-------------|--------------|
 * | small    | 158 × 120   | —            |
 * | standard | 266 × 192   | 298 × 190    |
 * | large    | 266 × 350   | 298 × 402    |
 *
 * Heights are controlled by content, not the carousel.
 * First position: first card flush with content edge (pl-4 on container).
 * Subsequent positions: cards snap at content edge, previous cards bleed left.
 * Trailing 16px comes from a right margin on the last slide ([&>:last-child]:mr-4):
 * Embla measures end padding from the last slide's margin, not container padding,
 * so container pr-* is clipped by containScroll and the last card would otherwise
 * stick to the viewport edge.
 * Gap: 16px between cards.
 *
 * Snap inset: 16px from viewport edge for snap 1+ — flush with the title's left
 * edge (pl-4), no previous-card peek. Snap 0 is also at the content edge (pl-4).
 * Override via opts={{ align: () => N }} for other values.
 */

const CARD_SNAP_INSET = 16;
const cardAlign = () => CARD_SNAP_INSET;

const cardOpts: Partial<EmblaOptionsType> = {
  align: cardAlign,
  containScroll: 'keepSnaps',
  slidesToScroll: 'auto',
};

export const carouselPresets: Record<CarouselPresetName, CarouselPresetConfig> = {
  fullWidth: {
    opts: { loop: true, align: 'center' },
    // Use flex shorthand to avoid sub-pixel rendering gaps between slides.
    // The shorthand form resolves as a single browser computation, unlike individual
    // shrink-0/grow-0/basis-full which can produce fractional width differences.
    classNames: { item: 'flex-[0_0_100%]' },
    // Disabled to prevent sub-pixel artifact (1px line from next slide)
    backfaceHidden: false,
  },
  cardSmall: {
    opts: cardOpts,
    classNames: { container: 'gap-4 pl-4 [&>:last-child]:mr-4', item: 'basis-[158px]' },
  },
  cardStandard: {
    opts: cardOpts,
    classNames: {
      container: 'gap-4 pl-4 [&>:last-child]:mr-4',
      item: 'basis-[266px] md:basis-[298px]',
    },
  },
  cardLarge: {
    opts: cardOpts,
    classNames: {
      container: 'gap-4 pl-4 [&>:last-child]:mr-4',
      item: 'basis-[266px] md:basis-[298px]',
    },
  },
};
